import {
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@elaniintc/goes-design-system";
import type { NodeVariable, Variable } from "../../types";
import { getTypeIcon, getTypeRepresentation } from "../../if/utils";
import { useMemo } from "react";

export interface VariableSelectorProps {
  variables: NodeVariable[];
  selectedVariable: Variable | null;
  onSelectVariable: (variable: Variable | null) => void;
  triggerGroup?:
    | "middle"
    | "middle-up"
    | "middle-left"
    | "middle-right"
    | "down-left"
    | "down-right"
    | "up-left"
    | "up-right"
    | "left"
    | "right"
    | "down";
  placeholder?: string;
  filterByType?: string; // Filtrar variables por tipo específico (legacy, un solo tipo)
  filterByTypes?: string[]; // Filtrar variables por múltiples tipos compatibles
}

export const VariableSelector = ({
  variables,
  selectedVariable,
  onSelectVariable,
  triggerGroup: group,
  placeholder = "Seleccione una variable",
  filterByType,
  filterByTypes,
}: VariableSelectorProps) => {
  // Flatten all variables across nodes and enrich with node_id
  const allVariables = useMemo<Variable[]>(() => {
    const flatVariables = (variables || []).flatMap((node) =>
      (node.variables || []).map((v) => ({
        ...v,
        node_id: node.nodeId,
        ref_name: node.ref_name,
      })),
    );

    // Si hay un filtro por múltiples tipos, aplicarlo primero
    if (filterByTypes && filterByTypes.length > 0) {
      return flatVariables.filter((v) =>
        filterByTypes.includes(v.type.toLowerCase()),
      );
    }

    // Si hay un filtro por tipo único (legacy), aplicarlo
    if (filterByType) {
      return flatVariables.filter((v) => v.type === filterByType);
    }

    return flatVariables;
  }, [variables, filterByType, filterByTypes]);

  return (
    <Select
      onValueChange={(val) => {
        const variable = allVariables.find((v) => v.id === val) || null;
        onSelectVariable(variable);
      }}
      value={selectedVariable?.id || ""}
    >
      <SelectTrigger className="min-w-0 w-full flex" group={group}>
        <div className="w-full flex gap-3 items-center">
          {selectedVariable && (
            <Icon type="far" name={getTypeIcon(selectedVariable.type)} />
          )}
          <p className="truncate text-body">
            {selectedVariable?.name ?? placeholder}
          </p>
        </div>
      </SelectTrigger>
      <SelectContent className="max-h-[440px] overflow-y-auto border border-neutral py-2">
        {allVariables.map((variable) => (
          <SelectItem
            key={`${variable.id}-${variable.name}`}
            value={variable.id}
          >
            <div className="w-full flex items-center gap-3">
              <Icon type="far" name={getTypeIcon(variable.type)} />
              <div className="flex gap-2 items-center">
                <span className="text-body font-semibold">{variable.name}</span>
                <span className="text-body-quiet">
                  {getTypeRepresentation(variable.type)}
                </span>
              </div>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
